// askSmart-ui/src/pages/smrtChequesQuoteDoc.ts
//
// Frontend-only PDF generator for the SMRT Cheques quote document. Captures
// a hidden HTML layout (rendered inside SMRTCheques.tsx) with html2canvas,
// embeds the resulting PNG into a single-page A4 PDF via jsPDF, and triggers
// a browser download. No backend, no headless browser, no print API.

import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

export interface QuoteDocData {
  clientName: string;
  siteName: string;
  projectReference: string;
  scope: string;
  baseAmount: number;
  shippingFee: number;
  markup: number;
  total: number;
}

// A4 in pixels at 96 DPI: 794 × 1123. We render the hidden layout at this
// width so the html2canvas snapshot maps 1:1 onto an A4 PDF page.
export const QUOTE_PAGE_WIDTH_PX = 794;

export async function downloadQuotePdf(
  element: HTMLElement,
  projectReference: string,
): Promise<void> {
  // scale=1.5 keeps text crisp at A4 sizes while shrinking the source canvas
  // (and the resulting JPEG) by ~44% vs scale=2. backgroundColor:#fff is
  // required because the hidden layout sits on the dark app background.
  const canvas = await html2canvas(element, {
    scale: 1.5,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
  });

  // JPEG at q=0.8 produces a noticeably smaller file than PNG with no visible
  // quality loss for this kind of black-text-on-white layout.
  const imgData = canvas.toDataURL("image/jpeg", 0.8);

  const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // Fit width to page; preserve aspect ratio. The total image is taller than
  // one page when the source DOM spans multiple A4-sized blocks — we paginate
  // by translating the same image upward by one pageHeight per page.
  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  // Drop trailing blank pages caused by sub-pixel overflow at the end of the
  // source canvas. Anything inside 5% of a page height is treated as
  // artifact, not real content, so we don't emit a near-empty trailing page.
  const trailingSlop = pageHeight * 0.05;
  const totalPages = Math.max(
    1,
    Math.ceil((imgHeight - trailingSlop) / pageHeight),
  );

  for (let i = 0; i < totalPages; i++) {
    if (i > 0) pdf.addPage();
    pdf.addImage(imgData, "JPEG", 0, -i * pageHeight, imgWidth, imgHeight);
  }

  const safeRef =
    (projectReference || "").replace(/[^A-Za-z0-9_-]+/g, "_") || "quote";
  pdf.save(`Quote_${safeRef}.pdf`);
}
